import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Briefcase,
  Plus,
  Search,
  Edit2,
  Trash2,
  Power,
  AlertCircle,
  CheckCircle,
  Users,
  X,
} from 'lucide-react'
import { positionService } from '../../services/positionService'
import { Position } from '../../types'
import { ConfirmDialog, useConfirmDialog } from '../../components/ui/ConfirmDialog'
import { extractApiErrors } from '../../utils/errorHandler'

// Modal de formulario para crear/editar
interface PositionFormModalProps {
  position: Position | null
  onClose: () => void
  onSuccess: () => void
}

function PositionFormModal({ position, onClose, onSuccess }: PositionFormModalProps) {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: position?.name || '',
    isActive: position?.isActive ?? true,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (position) {
        await positionService.update(position.id, formData)
        toast.success('Puesto actualizado')
      } else {
        await positionService.create(formData)
        toast.success('Puesto creado')
      }
      onSuccess()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-dark-card rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold">
            {position ? 'Editar Puesto' : 'Nuevo Puesto'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="label">Nombre del puesto *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              placeholder="Ej: OPERADOR"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              El nombre se guardará en mayúsculas
            </p>
          </div>

          {position && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
                Puesto activo
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : position ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function PositionsPage() {
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingPosition, setEditingPosition] = useState<Position | null>(null)

  const confirmDialog = useConfirmDialog()

  useEffect(() => {
    loadPositions()
  }, [])

  const loadPositions = async () => {
    try {
      setLoading(true)
      const data = await positionService.getAll(true)
      setPositions(data)
    } catch (error) {
      toast.error('Error al cargar los puestos')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (pos: Position) => {
    try {
      await positionService.update(pos.id, { isActive: !pos.isActive })
      toast.success(`Puesto ${pos.isActive ? 'desactivado' : 'activado'}`)
      loadPositions()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    }
  }

  const handleDelete = async (pos: Position) => {
    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar puesto',
      message: `¿Está seguro de eliminar "${pos.name}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await positionService.delete(pos.id)
      toast.success('Puesto eliminado')
      loadPositions()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    }
  }

  const handleEdit = (pos: Position) => {
    setEditingPosition(pos)
    setShowFormModal(true)
  }

  const handleCreate = () => {
    setEditingPosition(null)
    setShowFormModal(true)
  }

  const handleFormSuccess = () => {
    setShowFormModal(false)
    setEditingPosition(null)
    loadPositions()
  }

  // Filter positions
  const filteredPositions = positions.filter((pos) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      if (!pos.name.toLowerCase().includes(search)) {
        return false
      }
    }
    if (filterActive !== '' && pos.isActive !== filterActive) return false
    return true
  })

  return (
    <div className="animate-slide-in">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Briefcase className="h-6 w-6" />
              <span className="hidden sm:inline">Puestos de Trabajo</span>
              <span className="sm:hidden">Puestos</span>
            </h1>
            <p className="page-subtitle">
              Gestiona los puestos de trabajo de la empresa
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCreate}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuevo Puesto</span>
              <span className="sm:hidden">Nuevo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <select
            value={filterActive === '' ? '' : filterActive ? 'true' : 'false'}
            onChange={(e) =>
              setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
            }
            className="input sm:w-40"
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Positions List */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </div>
      ) : filteredPositions.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No hay puestos configurados
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Crea un nuevo puesto para comenzar
            </p>
            <button onClick={handleCreate} className="btn btn-primary">
              Crear primer puesto
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPositions.map((pos) => (
            <div
              key={pos.id}
              className={`card ${!pos.isActive ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`badge text-xs ${pos.isActive ? 'badge-success' : 'badge-gray'}`}
                    >
                      {pos.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                    {pos.name}
                  </h3>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(pos)}
                    className={`p-2 rounded-lg transition-colors ${
                      pos.isActive
                        ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                        : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={pos.isActive ? 'Desactivar' : 'Activar'}
                  >
                    <Power className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(pos)}
                    className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(pos)}
                    className="p-2 rounded-lg text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {pos.employeeCount !== undefined && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Users className="h-4 w-4" />
                    <span>{pos.employeeCount} empleados asignados</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats summary */}
      {!loading && positions.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Briefcase className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {positions.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total de puestos
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-success-100 dark:bg-success-900/30">
              <CheckCircle className="h-6 w-6 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {positions.filter((pos) => pos.isActive).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Puestos activos
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {positions.reduce((sum, pos) => sum + (pos.employeeCount || 0), 0)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Empleados totales
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <PositionFormModal
          position={editingPosition}
          onClose={() => {
            setShowFormModal(false)
            setEditingPosition(null)
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog.dialogProps} />
    </div>
  )
}
